import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import { toastError } from "@/lib/toast-error";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useBulkAddCatalogProducts,
  usePreviewBulkAddCatalogProducts,
  getListCatalogProductsQueryKey,
} from "@workspace/api-client-react";
import type { PreviewBulkAddCatalogProductsResult } from "@workspace/api-client-react";

export function parseBulkNames(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function removeNameFromText(text: string, target: string): string {
  // Tokens are split per parseBulkNames semantics: by newline, then by comma.
  // We match on the trimmed token, so an empty target strips blank tokens
  // (consecutive commas, blank lines) — which is what an empty-name invalid
  // entry maps to.
  const t = target.trim();
  return text
    .split("\n")
    .map((line) =>
      line
        .split(",")
        .filter((tok) => tok.trim() !== t)
        .join(","),
    )
    .filter((line) => line.trim().length > 0)
    .join("\n");
}

export function replaceNameInText(text: string, oldName: string, newName: string): string {
  const o = oldName.trim();
  // For empty oldName there is no concrete token to replace in place, so the
  // safest deterministic fix is to append the new name on its own line.
  if (!o) {
    if (!newName) return text;
    return text.length === 0 || text.endsWith("\n") ? `${text}${newName}` : `${text}\n${newName}`;
  }
  return text
    .split("\n")
    .map((line) =>
      line
        .split(",")
        .map((tok) => (tok.trim() === o ? tok.replace(tok.trim(), newName) : tok))
        .join(","),
    )
    .join("\n");
}

export interface BulkAddProductsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BulkAddProductsDialog({ open, onOpenChange }: BulkAddProductsDialogProps) {
  const queryClient = useQueryClient();
  const bulkAddProducts = useBulkAddCatalogProducts();
  const previewBulkAdd = usePreviewBulkAddCatalogProducts();

  const [bulkText, setBulkText] = useState("");
  const [bulkInvalidItems, setBulkInvalidItems] = useState<{ name: string; reason: string }[]>([]);
  const [bulkPreview, setBulkPreview] = useState<PreviewBulkAddCatalogProductsResult | null>(null);
  const [editingInvalidIndex, setEditingInvalidIndex] = useState<number | null>(null);
  const [editingInvalidValue, setEditingInvalidValue] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  function reset() {
    setBulkText("");
    setBulkInvalidItems([]);
    setBulkPreview(null);
    setEditingInvalidIndex(null);
    setEditingInvalidValue("");
  }

  function handleOpenChange(next: boolean) {
    onOpenChange(next);
    if (!next) reset();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      setBulkText((prev) => (prev.trim() ? prev + "\n" + text : text));
      setBulkPreview(null);
      setBulkInvalidItems([]);
      setEditingInvalidIndex(null);
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  function handlePreview() {
    const names = parseBulkNames(bulkText);
    if (names.length === 0) {
      toast.error("No product names to preview. Enter at least one name.");
      return;
    }
    setBulkInvalidItems([]);
    setEditingInvalidIndex(null);
    previewBulkAdd.mutate(
      { data: { names } },
      {
        onSuccess: (result) => {
          setBulkPreview(result);
          setBulkInvalidItems(result.invalid ?? []);
        },
        onError: (err: unknown) => toastError(err, "Failed to preview bulk add"),
      },
    );
  }

  function handleConfirm() {
    if (!bulkPreview || bulkPreview.newNames.length === 0) return;
    bulkAddProducts.mutate(
      { data: { names: bulkPreview.newNames } },
      {
        onSuccess: (result) => {
          queryClient.invalidateQueries({ queryKey: getListCatalogProductsQueryKey() });
          const parts: string[] = [];
          if (result.created > 0) parts.push(`${result.created} added`);
          if (result.skippedDuplicates > 0)
            parts.push(
              `${result.skippedDuplicates} duplicate${result.skippedDuplicates !== 1 ? "s" : ""} skipped`,
            );
          if (result.invalid.length > 0) parts.push(`${result.invalid.length} invalid`);
          toast.success(parts.length > 0 ? parts.join(", ") : "No changes made");
          reset();
          onOpenChange(false);
        },
        onError: (err: unknown) => toastError(err, "Failed to bulk add products"),
      },
    );
  }

  function handleRemoveInvalid(name: string) {
    setBulkText((prev) => removeNameFromText(prev, name));
    setBulkPreview(null);
    setBulkInvalidItems((prev) => prev.filter((it) => it.name.trim() !== name.trim()));
    setEditingInvalidIndex(null);
    setEditingInvalidValue("");
  }

  function handleRemoveAllInvalid() {
    if (bulkInvalidItems.length === 0) return;
    setBulkText((prev) =>
      bulkInvalidItems.reduce((acc, it) => removeNameFromText(acc, it.name), prev),
    );
    setBulkPreview(null);
    setBulkInvalidItems([]);
    setEditingInvalidIndex(null);
    setEditingInvalidValue("");
  }

  function startEditingInvalid(index: number, currentName: string) {
    setEditingInvalidIndex(index);
    setEditingInvalidValue(currentName);
  }

  function cancelEditingInvalid() {
    setEditingInvalidIndex(null);
    setEditingInvalidValue("");
  }

  function saveInvalidEdit(index: number, oldName: string) {
    const next = editingInvalidValue.trim();
    if (!next) {
      toast.error("Name cannot be empty. Use Remove instead.");
      return;
    }
    setBulkText((prev) => replaceNameInText(prev, oldName, next));
    setBulkPreview(null);
    setBulkInvalidItems((prev) => prev.filter((_, i) => i !== index));
    setEditingInvalidIndex(null);
    setEditingInvalidValue("");
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Bulk add products</DialogTitle>
          <DialogDescription>
            Paste product names below (one per line or comma-separated), or upload a <code>.csv</code> / <code>.txt</code> file. Duplicates and invalid entries will be reported.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Textarea
            data-testid="bulk-add-textarea"
            placeholder={"Product A\nProduct B\nProduct C"}
            value={bulkText}
            onChange={(e) => {
              setBulkText(e.target.value);
              if (bulkPreview) setBulkPreview(null);
              // Manual textarea edits invalidate the previously-reported
              // invalid list so stale Remove/Edit actions can't run.
              if (bulkInvalidItems.length > 0) setBulkInvalidItems([]);
              if (editingInvalidIndex !== null) {
                setEditingInvalidIndex(null);
                setEditingInvalidValue("");
              }
            }}
            rows={8}
            className="font-mono text-sm resize-none"
          />
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              className="hidden"
              onChange={handleFileChange}
            />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
              Upload .csv / .txt
            </Button>
            {bulkText.trim() && (
              <span className="text-sm text-muted-foreground" data-testid="bulk-add-parsed-count">
                {parseBulkNames(bulkText).length} name{parseBulkNames(bulkText).length !== 1 ? "s" : ""} parsed
              </span>
            )}
          </div>
          {bulkInvalidItems.length > 0 && (
            <div
              className="rounded-md border border-destructive/40 bg-destructive/5 p-3 space-y-2"
              data-testid="bulk-add-invalid"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-destructive">
                  Invalid entries — fix or remove, then re-preview:
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRemoveAllInvalid}
                  data-testid="bulk-add-remove-all-invalid"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/30"
                >
                  Remove all invalid
                </Button>
              </div>
              <ul className="text-sm space-y-1.5">
                {bulkInvalidItems.map((item, i) => {
                  const isEditing = editingInvalidIndex === i;
                  const displayName = item.name.trim() ? item.name : "(empty)";
                  return (
                    <li
                      key={i}
                      className="flex flex-wrap items-center gap-2"
                      data-testid={`bulk-add-invalid-item-${i}`}
                    >
                      {isEditing ? (
                        <>
                          <Input
                            data-testid={`bulk-add-invalid-edit-input-${i}`}
                            value={editingInvalidValue}
                            onChange={(e) => setEditingInvalidValue(e.target.value)}
                            className="h-8 text-sm font-mono flex-1 min-w-0"
                            autoFocus
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="default"
                            onClick={() => saveInvalidEdit(i, item.name)}
                            data-testid={`bulk-add-invalid-save-${i}`}
                          >
                            Save
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={cancelEditingInvalid}
                            data-testid={`bulk-add-invalid-cancel-${i}`}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <div className="flex-1 min-w-0 text-muted-foreground">
                            <span className="font-medium text-foreground break-all">
                              {displayName}
                            </span>{" "}
                            — {item.reason}
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => startEditingInvalid(i, item.name)}
                            data-testid={`bulk-add-invalid-edit-${i}`}
                          >
                            Edit
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => handleRemoveInvalid(item.name)}
                            data-testid={`bulk-add-invalid-remove-${i}`}
                            className="text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/30"
                          >
                            Remove
                          </Button>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {bulkPreview && (
            <div className="rounded-md border bg-muted/30 p-3 space-y-3" data-testid="bulk-add-preview">
              <p className="text-sm font-medium" data-testid="bulk-add-preview-summary">
                Preview: {bulkPreview.newNames.length} new, {bulkPreview.duplicates.length} duplicate{bulkPreview.duplicates.length !== 1 ? "s" : ""}, {bulkPreview.invalid.length} invalid
              </p>
              {bulkPreview.newNames.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                    Will be added ({bulkPreview.newNames.length})
                  </p>
                  <ul className="text-sm max-h-32 overflow-y-auto list-disc list-inside" data-testid="bulk-add-new-list">
                    {bulkPreview.newNames.map((name, i) => (
                      <li key={i}>{name}</li>
                    ))}
                  </ul>
                </div>
              )}
              {bulkPreview.duplicates.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Will be skipped ({bulkPreview.duplicates.length})
                  </p>
                  <ul className="text-sm max-h-32 overflow-y-auto list-disc list-inside text-muted-foreground" data-testid="bulk-add-duplicates-list">
                    {bulkPreview.duplicates.map((d, i) => (
                      <li key={i}>
                        <span className="text-foreground">{d.name}</span> — {d.reason === "existing" ? "already in catalog" : "repeated in list"}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {bulkPreview.newNames.length === 0 && (
                <p className="text-sm text-muted-foreground">Nothing new to add.</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {bulkPreview ? (
            <>
              <Button
                variant="outline"
                onClick={() => setBulkPreview(null)}
                disabled={bulkAddProducts.isPending}
              >
                Edit list
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={bulkAddProducts.isPending || bulkPreview.newNames.length === 0}
                data-testid="bulk-add-confirm"
              >
                {bulkAddProducts.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                Confirm and add {bulkPreview.newNames.length > 0 ? `(${bulkPreview.newNames.length})` : ""}
              </Button>
            </>
          ) : (
            <Button
              onClick={handlePreview}
              disabled={previewBulkAdd.isPending || !bulkText.trim()}
              data-testid="bulk-add-preview-button"
            >
              {previewBulkAdd.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Preview
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
